import React, {
    Dispatch, SetStateAction, useEffect, useMemo, useRef, useState
} from 'react';

import i18n from '@nextroll/ar-i18n';
import { useContainerContext } from '@nextroll/ar-container-context';
import { Alerts, InlineAlert } from '@nextroll/ar-style-base';

import { useRoflStore } from 'store/roflStore';
import { WORKFLOW_BASE } from 'types/rofl';
import { ITriggerNodeConfig, TRIGGER_RULE_TYPES } from 'components/nodes/ContactTriggerNode/ContactTriggerNode.type';
import { getAccountList, getContactList } from 'api/cdpPlusApi';

import { SettingsPreviewTable, ISettingsPreviewTableCols } from './SettingsPreviewTable';
import SettingsSectionSchedule from './SettingsSectionSchedule';
import { SettingsSectionCreditUsage } from './SettingsSectionCreditUsage';


/**
 * this is the max number of contacts allowed to enroll automatically
 */
const ENROLLMENT_LIMIT = 25000;

/**
 * this is the max number of contacts shown in the preview table
 */
const PREVIEW_SIZE = 5000;

/**
* default period for reentry when enabled
 */
const DEFAULT_REENTRY_PERIOD = 'P1D';

interface IProps {
    // the readonly mode is for editing an existing workflow, when
    // enabling the "existing contacts enrollment" section is hidden
    readonly?: boolean;
    name: string;
    setName: Dispatch<SetStateAction<string>>;
    schedule: string;
    setSchedule: Dispatch<SetStateAction<string>>;
    includeCurrentEntities: boolean;
    setIncludeCurrentEntities: Dispatch<SetStateAction<boolean>>;
    reentryAfter?: string;
    setReentryAfter: Dispatch<SetStateAction<string|undefined>>;
    onBack?: () => void;
}

export const SettingsPanel = ({
    readonly,
    name,
    setName,
    schedule,
    setSchedule,
    includeCurrentEntities,
    setIncludeCurrentEntities,
    reentryAfter,
    setReentryAfter,
    onBack,
}: IProps) => {
    const { advertisable: { eid: advertisableEid } = {} } = useContainerContext();

    const [base, contentData, triggerResults] = useRoflStore(state => [
        state.base,
        state.contentData,
        state.triggerResults,
    ]);

    // basic state variable
    const [expandPreview, setExpandPreview] = useState(false);

    // state variables for preview table
    const [data, setData] = useState([] as ISettingsPreviewTableCols[]);
    const [dataSize, setDataSize] = useState(0); // only when using existing contact lists
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);

    const triggerData = contentData.trigger?.data as ITriggerNodeConfig;
    const rules = useMemo(() => {
        if (base === WORKFLOW_BASE.ACCOUNTS) {
            // account-list rules + exclusions
            return {
                list_rule: triggerData?.config?.list_rule,
                not_in_advertising_filter: triggerData?.config?.not_in_advertising_filter,
                not_in_crm_accounts: triggerData?.config?.not_in_crm_accounts,
                not_in_sales_marketing: triggerData?.config?.not_in_sales_marketing,
                not_in_intent: triggerData?.config?.not_in_intent,
                exclusions_types: triggerData?.config?.exclusions_types,

                // if `not_in_sales_marketing` is true, `not_in_sales_marketing_since` must be provided
                ...(triggerData?.config?.not_in_sales_marketing ? { not_in_sales_marketing_since: 'P30D' } : {}),

                // if `not_in_intent` is true, `not_in_intent_since` must be provided
                ...(triggerData?.config?.not_in_intent ? { not_in_intent_since: 'P30D' } : {}),
            };
        }
        return {
            list_rule: triggerData?.ruleType === TRIGGER_RULE_TYPES.LIST_MEMBERSHIP
                ? triggerData?.selectedContactList?.rules
                : triggerData?.rules,
        };
    }, [base, triggerData]);

    /**
     * calculate the number of contacts or accounts
     */
    const totalCount = useMemo(() => {
        if (base === WORKFLOW_BASE.CONTACTS && triggerData?.ruleType === TRIGGER_RULE_TYPES.LIST_MEMBERSHIP) {
            return dataSize || 0;
        }
        return triggerResults?.totalCount || 0;
    }, [base, triggerData?.ruleType, triggerResults?.totalCount, dataSize]);

    /**
     * load the content of the preview table of accounts or contacts
     */
    const preview = useRef(async () => {
        if (!advertisableEid || loading) {
            return; // not a good time; do nothing
        }
        setLoading(true);
        try {
            const get = base === WORKFLOW_BASE.ACCOUNTS ? getAccountList : getContactList;
            const response = await get(advertisableEid, rules || {});
            if (response && Array.isArray(response)) {
                setData(response.slice(0, PREVIEW_SIZE));
                setDataSize(response.length);
            }
        } catch (ex: any) {
            const errorMsg = ex?.message || JSON.stringify(ex);
            console.error(errorMsg);
            setError(errorMsg);
        }
        setLoading(false);
    });

    /**
     * make sure to load the preview table after the panel is mounted
     */
    useEffect(() => {
        preview.current();
    }, []);

    /**
     * if the number of items exceeds the enrollment limit, set 'includeCurrentEntities' to false
     */
    const isExceedEnrollmentLimit = useMemo(() => totalCount > ENROLLMENT_LIMIT, [totalCount]);
    useEffect(() => {
        if (isExceedEnrollmentLimit) {
            setIncludeCurrentEntities(false);
        }
    }, [isExceedEnrollmentLimit, setIncludeCurrentEntities]);

    /**
     * the warning message only when enrollment limit exceeded
     */
    const enrollmentWarning = useMemo(() => {
        if (base !== WORKFLOW_BASE.CONTACTS || !isExceedEnrollmentLimit || loading) {
            return null;
        }
        const limit = i18n.numeral(ENROLLMENT_LIMIT).format('0,0');
        const message = i18n.gettext('AdRoll Workflows cannot enroll more than {{ limit }} existing contacts.', { limit });

        return (
            <div className='ar-alerts-container'>
                <div className='ar-alert ar-alert-warning'>
                    <div className='ar-alert-content-container'>
                        <i className='ar-alert-icon ar-icon-danger' />
                        <div className='ar-alert-text'>
                            <span className='ar-alert-title'>
                                {i18n.gettext('The amount of existing contacts has exceeded the enrollment limit')}
                            </span>
                            <span className='ar-alert-message'>
                                {message}
                                &nbsp;
                                {i18n.gettext('It will only enroll contacts who meet the trigger criteria after turning on.')}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        );
    }, [base, isExceedEnrollmentLimit, loading]);

    /**
     * includeCurrentEntities ratio options only when enrollment limit not exceeded
     */
    const enrollmentOptions = useMemo(() => base === WORKFLOW_BASE.CONTACTS && !isExceedEnrollmentLimit && !loading && (
        <>
            <input
                type='radio'
                id='workflows-canvas-setting-panel-trigger-use-enroll-contacts-true'
                className='ar-radio-button'
                value='true'
                checked={includeCurrentEntities}
                onChange={() => !includeCurrentEntities && setIncludeCurrentEntities(true)}
            />
            <label htmlFor='workflows-canvas-setting-panel-trigger-use-enroll-contacts-true'>
                {i18n.gettext('Yes, enroll the contacts who meet the criteria at this time.')}
            </label>
            <br />
            <input
                type='radio'
                id='workflows-canvas-setting-panel-trigger-use-enroll-contacts-false'
                className='ar-radio-button'
                value='false'
                checked={!includeCurrentEntities}
                onChange={() => includeCurrentEntities && setIncludeCurrentEntities(false)}
            />
            <label htmlFor='workflows-canvas-setting-panel-trigger-use-enroll-contacts-false'>
                {i18n.gettext('No, skip the contacts who meet the criteria at this time. They may still be enrolled at a later time if you enable Re-enrollment.')}
            </label>
        </>
    ), [base, isExceedEnrollmentLimit, loading, includeCurrentEntities, setIncludeCurrentEntities]);

    /**
     * This is the React component of the contact list preview table.
     */
    const previewTable = useMemo(() => (
        <SettingsPreviewTable
            base={base}
            data={data}
            total={totalCount}
            loading={loading}
            advertisableEid={advertisableEid}
            contactListEid={base === WORKFLOW_BASE.CONTACTS ? triggerData?.contact_list_eid : undefined}
        />
    ), [base, data, totalCount, loading, advertisableEid, triggerData?.contact_list_eid]);

    /**
     * below are sections in the settings page
     */
    const sectionName = useMemo(() => (
        <>
            <label className='control-label'>{i18n.gettext('Workflow Name')}</label>
            <input
                data-testid='workflow-settings-name-input'
                type='text'
                value={name || ''}
                className='form-control'
                maxLength={200}
                placeholder={i18n.gettext('Workflow Name')}
                onChange={e => setName(e.target.value)}
                onBlur={e => !e.target.value && setName('My Workflow')}
            />
        </>
    ), [name, setName]);

    const sectionEnrollCriteria = useMemo(() => !readonly && (
        <>
            <p>
                <strong>
                    {base === WORKFLOW_BASE.CONTACTS
                        ? i18n.gettext('Enrollment of Contacts that Currently Match Criteria')
                        : i18n.gettext('Enrollment of Accounts that Currently Match Criteria')}
                </strong>
            </p>
            <p>
                {loading
                    ? <span className='loading-spinner-small'>&nbsp;</span>
                    : <strong>{i18n.numeral(totalCount).format('0,0')}</strong>}
                &nbsp;
                {base === WORKFLOW_BASE.CONTACTS
                    ? i18n.gettext('contacts meet the trigger criteria at this time.')
                    : i18n.gettext('accounts meet the trigger criteria at this time.')}
                &nbsp;
                {base === WORKFLOW_BASE.ACCOUNTS && i18n.gettext('These will be enrolled when the workflow is turned on.') /* only for accounts */}
                {!!enrollmentOptions && i18n.gettext('Do you want to enroll these contacts?') /* only for contacts */}
            </p>

            {enrollmentWarning /* only appear when enrollment limit exceeded */}
            {enrollmentOptions /* only appear when enrollment limit not exceeded */}
        </>
    ), [base, readonly, loading, totalCount, enrollmentWarning, enrollmentOptions]);

    const sectionPreviewTable = useMemo(() => !readonly && (
        <>
            {error && (
                <InlineAlert
                    type={Alerts.ALERT_TYPE_WARNING}
                    title={base === WORKFLOW_BASE.ACCOUNTS ? i18n.gettext('Account preview unavailable') : i18n.gettext('Contact preview unavailable')}
                    message={base === WORKFLOW_BASE.ACCOUNTS
                        ? i18n.gettext('We\'re experiencing a temporary issue fetching account details. Please try again in a few minutes.')
                        : i18n.gettext('We\'re experiencing a temporary issue fetching contact details. Please try again in a few minutes.')}
                    showIcon
                />
            )}

            {!loading && (
                <button
                    role='button'
                    className='btn btn-link workflows-canvas-setting-panel-btn-expand-preview'
                    disabled={loading}
                    onClick={() => setExpandPreview(!expandPreview)}
                    type='button'
                >
                    <span className={`fa fa-caret-down ar-select-caret caret-${expandPreview ? 'up' : 'down'}`} />
                    {base === WORKFLOW_BASE.CONTACTS ? i18n.gettext('Preview contacts') : i18n.gettext('Preview accounts')}
                </button>
            )}

            {/* contact list preview table */}
            {expandPreview && previewTable}
        </>
    ), [base, readonly, error, loading, expandPreview, previewTable, setExpandPreview]);

    const sectionEnrollContactOrAccount = useMemo(() => !readonly && (
        <>
            {sectionEnrollCriteria}
            {sectionPreviewTable}
        </>
    ), [readonly, sectionEnrollCriteria, sectionPreviewTable]);

    const sectionEnrollSchedule = useMemo(() => (
        <SettingsSectionSchedule schedule={schedule} setSchedule={setSchedule} base={base} readonly={readonly} />
    ), [base, schedule, setSchedule, readonly]);

    const sectionCreditUsage = useMemo(() => base === WORKFLOW_BASE.ACCOUNTS && (
        <SettingsSectionCreditUsage advertisableEid={advertisableEid} readonly={readonly} />
    ), [base, advertisableEid, readonly]);

    const sectionLegalWarning = useMemo(() => base === WORKFLOW_BASE.ACCOUNTS && (
        <small>
            {i18n.gettext(
                'NextRoll\'s Contact Data Discovery Services are governed by the <AgreementLink>Contact Discovery '
                + 'Services Addendum</AgreementLink>. By using NextRoll\'s Contact Data Discovery Services, Customer '
                + 'agrees to be bound by the terms and conditions of the Addendum.',
                {
                    AgreementLink: (
                        <a
                            className='services-addendum-notification'
                            href='https://www.nextroll.com/terms/contact-data'
                            target='_blank'
                            rel='noopener noreferrer'
                        />
                    ),
                }
            )}
        </small>
    ), [base]);

    /**
     * Re-enrollment configuration. If the user specifies they want to allow re-enrollment, we let them specify
     * a number of days up to 3 digits. The number must be positive and valid. If it doesn't meet
     * that criteria, the input is reset to P1D on blur, similar to how the workflow name works.
     */
    const sectionReEnroll = useMemo(() => (
        <>
            <p><strong>{i18n.gettext('Re-enrollment')}</strong></p>
            <p>
                {base === WORKFLOW_BASE.CONTACTS
                    ? i18n.gettext('Do you want to allow contacts to re-enter this workflow once they have been processed?')
                    : i18n.gettext('Do you want to allow accounts to re-enter this workflow once they have been processed?')}
            </p>
            <input
                type='radio'
                id='workflows-canvas-setting-panel-reentry-no'
                data-testid='workflows-canvas-setting-panel-reentry-no'
                className='ar-radio-button'
                value='false'
                checked={!reentryAfter}
                onChange={() => !!reentryAfter && setReentryAfter(undefined)}
            />
            <label htmlFor='workflows-canvas-setting-panel-reentry-no'>
                {base === WORKFLOW_BASE.CONTACTS
                    ? i18n.gettext('No, qualified contacts should only be processed by this workflow once.')
                    : i18n.gettext('No, qualified accounts should only be processed by this workflow once.')}
            </label>
            <br />
            <input
                type='radio'
                id='workflows-canvas-setting-panel-reentry-yes'
                data-testid='workflows-canvas-setting-panel-reentry-yes'
                className='ar-radio-button'
                value='true'
                checked={!!reentryAfter}
                onChange={() => !reentryAfter && setReentryAfter(DEFAULT_REENTRY_PERIOD)}
            />
            <label htmlFor='workflows-canvas-setting-panel-reentry-yes'>
                {i18n.gettext(
                    base === WORKFLOW_BASE.CONTACTS
                        ? 'Yes, allow qualified contacts to re-enter this workflow after <DAYS /> day(s) from their last enrollment date.'
                        : 'Yes, allow qualified accounts to re-enter this workflow after <DAYS /> day(s) from their last enrollment date.',
                    {
                        DAYS: (
                            <input
                                data-testid='workflows-canvas-setting-panel-reentry-input'
                                type='text'
                                disabled={!reentryAfter}
                                maxLength={3}
                                className='form-control workflows-canvas-setting-panel-reentry-input'
                                value={(reentryAfter || DEFAULT_REENTRY_PERIOD).slice(1, -1)}
                                onChange={e => setReentryAfter(`P${e.target.value}D`)}
                                onBlur={e => {
                                    const days = Number(e.target.value);
                                    if (!Number.isInteger(days) || days <= 0) {
                                        setReentryAfter(DEFAULT_REENTRY_PERIOD);
                                    }
                                }}
                            />
                        )
                    }
                )}
            </label>
        </>
    ), [base, reentryAfter, setReentryAfter]);

    return (
        <div className='workflows-canvas-setting-panel'>
            <div className='workflows-canvas-setting-panel-container'>
                <button
                    className='btn btn-link workflows-canvas-setting-panel-btn-go-back'
                    onClick={onBack}
                    type='button'
                >
                    <span className='fa fa-arrow-left' />
                    {i18n.gettext('Back To Builder')}
                </button>

                {[
                    sectionName,
                    sectionEnrollSchedule,
                    sectionEnrollContactOrAccount,
                    sectionReEnroll,
                    sectionCreditUsage,
                    sectionLegalWarning,
                ].filter(Boolean).map((section, i) => (
                    <div
                        key={`workflows-canvas-setting-panel-section-${i.toString()}`}
                        className='workflows-canvas-setting-panel-section'
                    >
                        {section}
                        <hr />
                    </div>
                ))}
            </div>
        </div>
    );
};
